import Phaser from 'phaser';
import {Player} from '../entities/Player';
import {Room} from '../entities/Room';
import {GameConfig} from '../config/GameConfig';
import {moveTowards} from "../tools/Tools";
import {CardDeck} from '../entities/CardDeck';
import {Monster} from "../entities/Monster";
import {Fight} from "../entities/Fight";
import {FightResult} from "../entities/FightResult";
import {BossMonsterFrames, getMonsterFrames, SimpleMonsterFrames} from "../entities/MonsterFrames";
import {CardConfig, MonsterType} from "../entities/CardTypes";
import {generateRandomDeck, loopIncRoomCount, Loops, startRoomCount} from "../config/LoopConfig";
import {Stats} from "../entities/Stats";
import {getMonsterAttackSound} from "../entities/MonsterAttackSound";
import {Map} from "../entities/Map";

export default class GameScene extends Phaser.Scene {
  private player!: Player;
  private fight: Fight | undefined;
  private rooms: Room[] = [];
  private roomContainer!: Phaser.GameObjects.Container;
  private currentRoomIndex: number = -1;
  private isAutoMoving: boolean = false;
  private cardDeck!: CardDeck;
  private depthText!: Phaser.GameObjects.BitmapText;
  private goldText!: Phaser.GameObjects.BitmapText;
  private healthText!: Phaser.GameObjects.BitmapText;
  private statsText!: Phaser.GameObjects.BitmapText;
  private static loopIndex = 0
  private fightIndex = 0
  private particleContainer!: Phaser.GameObjects.Container
  private static nextRoomCardConfigs: (CardConfig | undefined)[] = []
  private currentRoomCardConfigs: CardConfig[] = []
  private lockers: any[] = []
  private monster: Monster | undefined = undefined
  private map: Map | undefined;

  constructor() {
    super({ key: 'GameScene' });
  }

  preload(): void {
    Player.idleFrames.forEach((frame) => {
      this.load.image(frame, `${frame}.png`);
    })

    Player.moveFrames.forEach((frame) => {
      this.load.image(frame, `${frame}.png`);
    })

    Player.attackFrames.forEach((frame) => {
      this.load.image(frame, `${frame}.png`);
    })

    SimpleMonsterFrames.idleFrames.forEach((frame) => {
      this.load.image(frame, `${frame}.png`);
    })

    SimpleMonsterFrames.attackFrames.forEach((frame) => {
      this.load.image(frame, `${frame}.png`);
    })

    BossMonsterFrames.idleFrames.forEach((frame) => {
      this.load.image(frame, `${frame}.png`);
    })

    BossMonsterFrames.attackFrames.forEach((frame) => {
      this.load.image(frame, `${frame}.png`);
    })

    this.load.image('room', 'room.png');
    this.load.image('room_start', 'room_start.png');
    this.load.image('room_sanctuary', 'room_sanctuary.png');
    this.load.image('room_forge', 'room_forge.png');
    this.load.image('room_simple', 'room_simple.png');
    this.load.image('room_boss', 'room_boss.png');
    this.load.image('card_sanctuary', 'card_sanctuary.png');
    this.load.image('card_simple', 'card_simple.png');
    this.load.image('card_forge', 'card_forge.png');
    this.load.bitmapFont('round_6x6', 'round_6x6.png', 'round_6x6.xml')

    const graphics = this.add.graphics();
    graphics.fillStyle(0xffffff);
    graphics.fillRect(0, 0, 1, 1);
    graphics.generateTexture('pixel', 1, 1);
    graphics.destroy();
    this.load.audio('sound_sanctuary', 'sanctuary.mp3');
    this.load.audio('sound_forge', 'forge.mp3');
    this.load.audio('sound_attack_player', 'attack_player.mp3');
    this.load.audio('sound_attack_simple', 'attack_simple.mp3');
    this.load.audio('sound_attack_boss', 'attack_boss.mp3');

    this.load.audio('sound_step_1', 'step_1.mp3');
    this.load.audio('sound_step_2', 'step_2.mp3');
    this.load.audio('sound_step_3', 'step_3.mp3');
    this.load.audio('sound_step_4', 'step_4.mp3');
  }

  create(): void {
    const { width, height } = this.scale;
    const { room, player } = GameConfig;
    this.currentRoomIndex = -1
    this.fightIndex = 0
    this.monster = undefined

    this.roomContainer = this.add.container(0, 0);

    const loopConfig = Loops[Math.min(GameScene.loopIndex, Loops.length - 1)]

    const roomSequence: {
      cardConfig?: CardConfig
    }[] = []

    if (GameScene.loopIndex === 0) {
      for (let i = 0; i < startRoomCount; ++i) {
        roomSequence.push({ cardConfig: undefined })
      }
    }
    else {
      GameScene.nextRoomCardConfigs.forEach((cardConfig) => {
        roomSequence.push({ cardConfig })
      })
    }
    
    roomSequence.push({ cardConfig: undefined })

    this.particleContainer = this.add.container(0, 0);
    this.particleContainer.setDepth(10001);

    const playerX = player.x;
    const playerY = height - (room.height / 2) - player.yOffset;
    this.player = new Player(this, playerX, playerY, this.particleContainer);

    this.rooms = []

    const roomX = 0.0;
    const roomY = room.height * 0.5;

    const startRoom = new Phaser.Physics.Arcade.Sprite(this, roomX, roomY, 'room_start')
    this.roomContainer.add(startRoom)

    roomSequence.forEach(({ cardConfig }, index) => {
      const roomX = (index + 1) * room.width;
      const roomY = room.height * 0.5;

      const isLastRoom = index === roomSequence.length - 1
      const newRoom = new Room(this, { type: isLastRoom ? 2 : 1, cardConfig, x: roomX, y: roomY }, this.roomContainer,
          isLastRoom, this.player.getStats());

      this.rooms.push(newRoom);
      this.roomContainer.add(newRoom);
    });

    this.roomContainer.x = room.width / 2;

    this.currentRoomCardConfigs = []

    this.cardDeck = new CardDeck(this, width / 2, height * 0.1, this.player.getStats());

    (loopConfig.startingDeck ?? generateRandomDeck()).forEach(cardConfig => {
      this.cardDeck.addCard(cardConfig);
    });

    this.createStatsDisplay();

    const startLocker = {}

    this.lock(startLocker)

    this.time.delayedCall(750, () => {
      this.startAutoMovement();
      this.unlock(startLocker)
    })

    this.cameras.main.fadeIn(500, 0, 0, 0, (camera: Phaser.Cameras.Scene2D.Camera, progress: number) => {
    });
    this.lockers = []
    this.map = new Map(this, this.rooms, this.currentRoomIndex)
  }

  private createStatsDisplay(): void {
    const { width, height } = this.scale;

    this.depthText = this.add.bitmapText(4, height - 45, 'round_6x6', this.getStatText()).setTint(0xeeeeee);
    this.depthText.setOrigin(0, 1);
    this.depthText.setScrollFactor(0);
    this.depthText.setDepth(1000);

    this.goldText = this.add.bitmapText(4, height - 36, 'round_6x6', this.getStatText()).setTint(0xffff00);
    this.goldText.setOrigin(0, 1);
    this.goldText.setScrollFactor(0);
    this.goldText.setDepth(1000);

    this.healthText = this.add.bitmapText(4, height - 27, 'round_6x6', this.getStatText()).setTint(0x00ff00);
    this.healthText.setOrigin(0, 1);
    this.healthText.setScrollFactor(0);
    this.healthText.setDepth(1000);
    
    this.statsText = this.add.bitmapText(4, height, 'round_6x6', this.getStatText()).setTint(0x00aaff);
    this.statsText.setOrigin(0, 1);
    this.statsText.setScrollFactor(0);
    this.statsText.setDepth(1000);
  }

  startAutoMovement(): void {
    this.lock(this.player)

    if (this.currentRoomIndex < this.rooms.length - 1) {
      this.isAutoMoving = true;
      this.player.startMoving()
      ++this.currentRoomIndex;
      this.rooms[this.currentRoomIndex].activateRoom()
    } else {
      this.cameras.main.fadeOut(1000, 0, 0, 0, (camera: Phaser.Cameras.Scene2D.Camera, progress: number) => {
        if (progress === 1) {
          this.registry.set('playerStats', this.player.getStats())
          GameScene.nextRoomCardConfigs = this.addEmptyRooms(this.currentRoomCardConfigs, loopIncRoomCount)
          ++GameScene.loopIndex
          this.scene.start('GameScene');
        }
      });
    }
  }

  stopAutoMovement(): void {
    this.isAutoMoving = false;
    this.player.stopMoving()
    this.unlock(this.player)
    const currentRoom = this.rooms[this.currentRoomIndex]
    this.cardDeck.setCurrentRoom(currentRoom);
    currentRoom.enterRoom()

    if (currentRoom.getConfig().type === 2) {
      this.startFight(MonsterType.BOSS)
    }
  }

  getRoomTargetX(roomIndex: number): number {
    return -this.rooms[roomIndex].startX + GameConfig.room.width / 2
  }

  update(time: number, delta: number): void {
    if (this.isAutoMoving) {
      const targetX = this.getRoomTargetX(this.currentRoomIndex)
      this.roomContainer.x = moveTowards(this.roomContainer.x, targetX, GameConfig.room.scrollSpeed * delta * 0.001)

      if (this.roomContainer.x === targetX) {
        this.stopAutoMovement();
      }
    }

    this.player.update(time, delta);
    this.updateStatsDisplay();

    if (this.fight !== undefined) {
      this.fight.update(time, delta)
      
      if (this.fight.getResult() === FightResult.WIN) {
        const fightWinLocker = {}
        this.lock(fightWinLocker)
        this.completeFight()
        ++this.fightIndex
        this.time.delayedCall(1000, () => {
          this.startAutoMovement()
          this.unlock(fightWinLocker)
        })
      }
      else if (this.fight.getResult() === FightResult.FAIL) {
        const fightFailLocker = {}
        this.lock(fightFailLocker)
        this.completeFight()
        this.player.dissolve()
        this.player.hide()
        this.time.delayedCall(750, () => {
          this.cameras.main.fadeOut(1000, 0, 0, 0, (camera: Phaser.Cameras.Scene2D.Camera, progress: number) => {
            if (progress === 1) {
              this.registry.set('playerStats', {
                ...this.player.getStats(),
                health: this.player.getStats().maxHealth
              })
              this.scene.start('GameScene');
            }
          });
        })
      }
    }

    if (this.monster !== undefined) {
      this.monster.update(time, delta)
    }

    if (this.currentRoomIndex !== -1) {
      if (this.map) {
        this.map.destroy();
      }
      this.map = new Map(this, this.rooms, this.currentRoomIndex);
    }
  }

  private formatValue(value: number): number {
    return Math.round(value * 10) / 10;
  }

  private getDepthText() {
    return `DEPTH: ${this.formatValue(GameScene.loopIndex + 1)}`;
  }

  private getGoldText() {
    const stats = this.player.getStats();

    return `GOLD: ${this.formatValue(stats.gold)}`;
  }

  private getHealthText() {
    const stats = this.player.getStats();

    return `HP: ${this.formatValue(stats.health)}/${this.formatValue(stats.maxHealth)}`;
  }

  private getStatText() {
    const stats = this.player.getStats();

    return `ATK: ${this.formatValue(stats.attack)}\n` +
        `SPD: ${this.formatValue(stats.speed)}\n` +
        `CRIT: ${this.formatValue(stats.crit)}%`;
  }

  private updateStatsDisplay(): void {
    this.depthText.setText(this.getDepthText());
    this.goldText.setText(this.getGoldText());
    this.healthText.setText(this.getHealthText());
    this.statsText.setText(this.getStatText());
  }

  computeMonsterStats(monsterType: MonsterType): Stats {
    const stats = {...GameConfig.monster.baseStats}

    for (let i = 0; i < GameScene.loopIndex; ++i) {
      stats.maxHealth += GameConfig.monster.loopStats.maxHealth
      stats.health += GameConfig.monster.loopStats.health
      stats.attack += GameConfig.monster.loopStats.attack
      stats.speed += GameConfig.monster.loopStats.speed
      stats.crit += GameConfig.monster.loopStats.crit
      stats.gold += GameConfig.monster.loopStats.gold
    }

    if (monsterType !== MonsterType.BOSS || GameConfig.monster.applyFightStatsForBoss) {
      for (let i = 0; i < this.fightIndex; ++i) {
        stats.maxHealth += GameConfig.monster.fightStats.maxHealth
        stats.health += GameConfig.monster.fightStats.health
        stats.attack += GameConfig.monster.fightStats.attack
        stats.speed += GameConfig.monster.fightStats.speed
        stats.crit += GameConfig.monster.fightStats.crit
        stats.gold += GameConfig.monster.fightStats.gold
      }
    }

    const multiplyMonsterStats = (multipliers: Stats) => {
      stats.maxHealth *= multipliers.maxHealth
      stats.health *= multipliers.health
      stats.attack *= multipliers.attack
      stats.speed *= multipliers.speed
      stats.crit *= multipliers.crit
      stats.gold *= multipliers.gold
    }

    stats.speed = Math.min(stats.speed, GameConfig.monster.maxSpeed)

    if (monsterType === MonsterType.SIMPLE) {
      multiplyMonsterStats(GameConfig.monster.simpleStatMultipliers)
    }
    else if (monsterType === MonsterType.BOSS) {
      multiplyMonsterStats(GameConfig.monster.bossStatMultipliers)
    }

    stats.speed = Math.min(stats.speed, GameConfig.monster.maxMultipliedSpeed)

    return stats
  }

  setMonster(monster: Monster) {
    this.monster = monster
  }

  startFight(monsterType: MonsterType) {
    const { width, height } = this.scale;
    const { room, monster } = GameConfig;
    const monsterX = monster.x;
    const monsterY = height - (room.height / 2) - monster.yOffset;
    const monsterEntity = new Monster(this, monsterX, monsterY, this.computeMonsterStats(monsterType),
        getMonsterFrames(monsterType), this.particleContainer, getMonsterAttackSound(monsterType), monsterType);
    this.fight = new Fight(this.player, monsterEntity, this);
    this.lock(this.fight)
  }

  completeFight() {
    this.unlock(this.fight)
    this.fight!.complete()
    this.fight = undefined
  }

  public addCardConfig(cardConfig: CardConfig): void {
    this.rooms[this.currentRoomIndex].getConfig().cardConfig = cardConfig
    this.currentRoomCardConfigs.push(cardConfig)
  }

  public addEmptyRooms(cardConfigs: CardConfig[], count: number): (CardConfig | undefined)[] {
    const result: (CardConfig | undefined)[] = [...cardConfigs];

    for (let i = 0; i < count; i++) {
      const randomPosition = Math.floor(Math.random() * (result.length + 1));
      result.splice(randomPosition, 0, undefined);
    }

    return result;
  }

  lock(locker: any) {
    this.lockers.push(locker)
    this.cardDeck.lock()
  }

  unlock(locker: any) {
    const index = this.lockers.indexOf(locker)

    if (index > -1) {
      this.lockers.splice(index, 1)
    }

    if (!this.getIsLocked()) {
      this.cardDeck.unlock()
    }
  }

  public getIsLocked() {
    return this.lockers.length > 0
  }

  public getPlayer() {
    return this.player
  }
}
